import DesignCard from 'components/card/DesignCard';
import DesignButton, {
  DesignButtonVariant,
} from 'components/button/DesignButton';
import Stepper from 'components/stepper/Stepper';
import { SpinnerIcon } from 'components/svgs';
import { CapacityPlanRunDetail } from 'core/graphql/CapacityPlanning/models';
import { CapacityPlanRunStatus } from 'core/graphql/types';
import { formatDateTime } from 'core/utils/DateUtils';
import { ReactNode } from 'react';
import GeocodeButton from './GeocodeButton';
import { CapacityPlanNextAction, deriveRunProgress } from './runProgress';
import RunStatusBadge from './RunStatusBadge';
import useCapacityPlanActions from './useCapacityPlanActions';

export interface RunHeaderProps {
  run: CapacityPlanRunDetail;
  canEdit: boolean;
}

/** The single next-action button, as the run's progress describes it. */
type PrimaryAction = {
  label: string;
  variant: DesignButtonVariant;
  /** Disabled for a reason the run itself carries, not for a missing permission. */
  blocked: boolean;
  /** 12px line under the button; mandatory whenever the button is blocked. */
  reason?: string;
  icon?: ReactNode;
};

const subtleText = 'text-light-black dark:text-sublte-white';

const primaryActionOf = (
  nextAction: CapacityPlanNextAction,
  blockedReason: string | undefined,
): PrimaryAction => {
  switch (nextAction) {
    case 'compute':
      return { label: 'Compute plan', variant: 'primary', blocked: false };
    case 'compute-again':
      return { label: 'Compute again', variant: 'secondary', blocked: false };
    case 'computing':
      return {
        label: 'Computing…',
        variant: 'primary',
        blocked: true,
        reason: 'Result opens here when done',
        icon: <SpinnerIcon />,
      };
    case 'queued':
      return {
        label: 'Queued…',
        variant: 'primary',
        blocked: true,
        reason: 'Waiting for a worker',
      };
    default:
      return {
        label: 'Compute plan',
        variant: 'primary',
        blocked: true,
        reason: blockedReason,
      };
  }
};

const canRetryFailedSolves = (run: CapacityPlanRunDetail) =>
  run.solveProgress.failed > 0 &&
  (run.status === CapacityPlanRunStatus.Computed ||
    run.status === CapacityPlanRunStatus.Failed);

/**
 * Identity and progress of the open Run, plus the actions it offers.
 * Compute lives here rather than in the Result tab, so there is a single place
 * that starts a solve. Failed solves can be retried from here on any tab.
 */
const RunHeader = ({ run, canEdit }: RunHeaderProps) => {
  const { steps, nextAction, blockedReason, exclusionHint } =
    deriveRunProgress(run);
  const {
    compute,
    requestGeocoding,
    retryFailedSolves,
    isComputing,
    isRequestingGeocoding,
    isRetryingSolves,
  } = useCapacityPlanActions(run.id);

  const action = primaryActionOf(nextAction, blockedReason);

  return (
    <DesignCard className="flex justify-between gap-6">
      <div className="w-80">
        <RunStatusBadge
          status={run.status}
          failedSolves={run.solveProgress.failed}
        />
        <div className="mt-2 text-base font-semibold">{run.fileName}</div>
        <div className={`mt-1 text-sm ${subtleText}`}>
          {run.totalDeliveries} deliveries · created{' '}
          {formatDateTime(new Date(run.createdAt))} by {run.createdBy} · run{' '}
          {run.id.slice(0, 8)}
        </div>
      </div>

      <div className="flex flex-grow items-start gap-3">
        <Stepper steps={steps} className="flex-grow" />
        {run.pendingGeocodes > 0 && (
          <span
            role="status"
            aria-label="Refreshing automatically"
            className="mt-1 flex items-center gap-2"
          >
            <span className="h-2 w-2 rounded-full bg-design-green ring-2 ring-design-green ring-opacity-40" />
            <span className={`text-xs ${subtleText}`}>live</span>
          </span>
        )}
      </div>

      <div className="flex w-64 flex-col items-end gap-2">
        {canRetryFailedSolves(run) && (
          <DesignButton
            variant="secondary"
            disabled={!canEdit || isRetryingSolves}
            onClickHandler={retryFailedSolves}
          >
            Retry failed solves ({run.solveProgress.failed})
          </DesignButton>
        )}
        <DesignButton
          variant={action.variant}
          disabled={!canEdit || action.blocked || isComputing}
          onClickHandler={compute}
        >
          {action.icon}
          {action.label}
        </DesignButton>
        {action.reason && (
          <span className={`text-right text-xs ${subtleText}`}>
            {action.reason}
          </span>
        )}
        {/* Rows without a pin do not hold the compute back; they are skipped. */}
        {exclusionHint && !action.blocked && (
          <span className={`text-right text-xs ${subtleText}`}>
            {exclusionHint}
          </span>
        )}
        <GeocodeButton
          status={run.status}
          missingCoordinates={run.missingCoordinates}
          canEdit={canEdit}
          isRequesting={isRequestingGeocoding}
          onConfirm={requestGeocoding}
        />
      </div>
    </DesignCard>
  );
};

export default RunHeader;
